import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { fetchWorkspaceRailCounts } from "../api/client";
import { useSocketContext } from "../context/SocketContext";
import { useAuthContext } from "../context/AuthContext";
import { buildAtendimentoCountRequest } from "../pages/Chat/utils/chatListContext";
import { readWorkspaceSessionJson, WS_KEY, getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { loadLastSeenClientAtMap } from "../utils/chatUnreadUtils";

export const WORKSPACE_RAIL_COUNTS_KEY = ["workspaceRailCounts"];

const POLL_MS = 30_000;

function loadDismissedAgendaIds() {
  try {
    const arr = readWorkspaceSessionJson(WS_KEY.SCHEDULE_REMINDER_DISMISSED, []);
    return Array.isArray(arr) ? arr.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function buildRailCountsQueryKey() {
  const atendimentoFilters = buildAtendimentoCountRequest();
  return [
    ...WORKSPACE_RAIL_COUNTS_KEY,
    {
      filters: [...(atendimentoFilters.filters || [])].sort(),
      searchTerm: atendimentoFilters.searchTerm || "",
      advanced: atendimentoFilters.advanced || {},
      ownershipOrGroups: atendimentoFilters.ownershipOrGroups || null,
    },
  ];
}

async function loadWorkspaceRailCounts() {
  const atendimentoFilters = buildAtendimentoCountRequest();
  return fetchWorkspaceRailCounts({
    atendimentoLastSeen: loadLastSeenClientAtMap(),
    agendaDismissed: loadDismissedAgendaIds(),
    atendimentoFilters,
  });
}

function mapCounts(counts) {
  return {
    service: Number(counts?.atendimentoUnread ?? 0),
    chat: Number(counts?.teamChatUnread ?? 0),
    inbox: Number(counts?.tasksOpen ?? 0),
    agenda: Number(counts?.agendaActive ?? 0),
  };
}

function isTransientAuthFailure(error) {
  const status = error?.response?.status ?? error?.status;
  const code = error?.response?.data?.code ?? error?.code;
  return (
    status === 503 ||
    code === "errors.auth.temporarily_unavailable" ||
    code === "errors.auth.forbidden"
  );
}

export function useWorkspaceRailCounts() {
  const queryClient = useQueryClient();
  const { socket } = useSocketContext();
  const { bearerToken } = useAuthContext() || {};
  const tenantSlug = getActiveWorkspaceSlug();
  const canFetch = Boolean(bearerToken && tenantSlug);
  const queryKey = buildRailCountsQueryKey();

  const { data } = useQuery(queryKey, async () => mapCounts(await loadWorkspaceRailCounts()), {
    enabled: canFetch,
    staleTime: 10_000,
    refetchInterval: canFetch ? POLL_MS : false,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
    retry: (failureCount, error) => {
      if (!isTransientAuthFailure(error)) return failureCount < 1;
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  useEffect(() => {
    if (!socket) return undefined;

    const invalidate = () => {
      queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
    };

    socket.on("message", invalidate);
    socket.on("request", invalidate);
    socket.on("messagesReload", invalidate);
    socket.on("team_message", invalidate);

    return () => {
      socket.off("message", invalidate);
      socket.off("request", invalidate);
      socket.off("messagesReload", invalidate);
      socket.off("team_message", invalidate);
    };
  }, [queryClient, socket]);

  return useMemo(
    () => ({
      service: Number(data?.service ?? 0),
      chat: Number(data?.chat ?? 0),
      inbox: Number(data?.inbox ?? 0),
      agenda: Number(data?.agenda ?? 0),
    }),
    [data]
  );
}

/** Invalida contadores quando filtros/busca do atendimento mudam. */
export function invalidateWorkspaceRailCounts(queryClient) {
  queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
}
